// External Dependencies:
import React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import FilePlayer from 'react-player/lib/players/FilePlayer'
import GoogleAnalytics from 'react-ga'

// Internal Dependencies:
import EventTracker from 'components/Tracker/EventTracker'
import { getUtcTimestampSeconds } from 'utilities/time'

// Redux:
import { goToPreviousTrack, goToNextTrack, getNowPlaying, setRewindWarningState, setBuffering, setMediaError, calculateSeekPercent } from 'actions/schedule'
import { updateAccount } from 'actions/site'

class SitePlayer extends React.Component {
	constructor() {
		super()

		this.state = {
			playerReady: false,
			volume: 1
		}
	}

	componentDidMount() {
		GoogleAnalytics.initialize( siteData.google_analytics_id )

		this.heartbeat = setInterval( () => {
			this.doHeartBeat()
		}, 1000 )

		this.analyticsInt = setInterval( () => {
			this.doAnalytics()
		}, 1000 * 60 )

		this.updateNowPlaying()

		setTimeout( () => {
			this.setState( {
				playerReady: true
			} )

			this.updatePlayer()
			this.props.calculateSeekPercent()
		}, 2000 )
	}

	componentWillUnmount() {
		clearInterval( this.heartbeat )
		clearInterval( this.fadeInterval )
		clearInterval( this.analyticsInt )

		if ( this.timeListeningInt ) {
			clearInterval( this.timeListeningInt )
		}
	}

	componentDidUpdate( prevProps ) {
		// Player:
		const { nowPlaying, playerOffset, showRewindWarning, setRewindWarningState, seekToDirection, seekSeconds, isFadingOut, isFullVolume, isPlaying } = this.props

		// Set player track:
		if ( nowPlaying && nowPlaying.audio_file_url && playerOffset != prevProps.playerOffset ) {
			this.updatePlayer()
		}

		// Rewind warning:
		if ( showRewindWarning ) {
			setTimeout( () => {
				setRewindWarningState( false )
			}, 4000 )
		}

		// Seek:
		if ( seekSeconds != prevProps.seekSeconds && 'rewind' == seekToDirection ) {
			this.rewind()
		} else if ( seekSeconds != prevProps.seekSeconds && 'fastForward' == seekToDirection ) {
			this.fastForward()
		} else if ( seekSeconds != prevProps.seekSeconds && 'seek' == seekToDirection ) {
			this.player.seekTo( seekSeconds )
		}

		// Fade out:
		if ( isFadingOut && ! prevProps.isFadingOut ) {
			this.fadeOut()
		}

		if ( isFullVolume && ! prevProps.isFullVolume ) {
			this.setState( {
				volume: 1
			} )
		}

		if ( isPlaying && ! prevProps.isPlaying ) {
			this.timeListeningInt = setInterval( () => {
				this.addTimeListening( 60 * 1 )
			}, 1000 * 60 * 1 )
		}

		if ( this.timeListeningInt && ! isPlaying ) {
			clearInterval( this.timeListeningInt )
		}
	}

	doHeartBeat() {
		const { nowPlaying, mediaError, isLive, isPlaying, isSeeking, calculateSeekPercent } = this.props

		// Do check for the current now playing data, in these cases:

		// Livestream - will not finish, preventing auto-advance.
		// Media errors when set to play live - will not auto-advance.
		// Tracks with an overlap - needs to advance at a specified time, not at end of track.
		if ( nowPlaying.is_livestream || ( mediaError && isLive ) || ( nowPlaying.is_partial_overlap ) ) {
			this.updateNowPlaying()
		}

		// Seek bar:
		if ( ! isSeeking && ( isPlaying || isLive ) ) {
			// Update the seek bar:
			calculateSeekPercent()
		}
	}

	doAnalytics() {
		const { location, isPlaying, nowPlaying, nowPlayingLive, isLive, timezoneType, userTimezoneOffset, siteId, secondsFromTrackStartToNow, seekSecondsForPercentCalcOnly, accountData, isEmbedded } = this.props

		if ( ! nowPlaying ) {
			return
		}

		const page = location.pathname
		const action = isPlaying ? 'Playing' : 'Not playing'

		let series = ''

		if ( nowPlaying.is_program && nowPlaying.series ) {
			series = nowPlaying.series
		}

		if ( ! nowPlaying.is_program ) {
			series = 'ad'
		}

		const utcTimestampSeconds = getUtcTimestampSeconds()
		const offset = -( parseInt( ( secondsFromTrackStartToNow - seekSecondsForPercentCalcOnly ), 10 ) )
		let nowPlayingTimeState

		if ( nowPlaying.is_share ) {
			nowPlayingTimeState = 'share'
		} else if ( nowPlaying.is_podcasts ) {
			nowPlayingTimeState = 'podcast'
		} else if ( nowPlaying.is_favourites ) {
			nowPlayingTimeState = 'favourite'
		} else if ( nowPlaying.is_recent_episodes ) {
			nowPlayingTimeState = 'recent_episode'
		} else if ( nowPlaying.start_timestamp_gmt == nowPlayingLive.start_timestamp_gmt ) {
			nowPlayingTimeState = 'current'
		} else if ( nowPlaying.end_timestamp_gmt < nowPlayingLive.start_timestamp_gmt ) {
			nowPlayingTimeState = 'past'
		} else if ( nowPlaying.start_timestamp_gmt > nowPlayingLive.end_timestamp_gmt ) {
			nowPlayingTimeState = 'future'
		}

		GoogleAnalytics.event( {
			label: 'Player',
			category: 'Player',
			action: action,

			dimension1: series,
			dimension2: nowPlaying.title,
			dimension3: isLive,

			dimension4: siteId,
			dimension5: timezoneType,
			dimension6: userTimezoneOffset,
			dimension7: isEmbedded
		} )

		EventTracker( accountData, {
			event_type: 'Player',
			action: action,
			page: page,

			series: series,
			now_playing_title: nowPlaying.title,
			is_live: isLive,
			now_playing_offset_seconds: offset,
			now_playing_time_state: nowPlayingTimeState,

			site_id: siteId,
			timezone_type: timezoneType,
			user_timezone_offset: userTimezoneOffset,
			is_embedded: isEmbedded
		} )
	}

	addTimeListening( time ) {
		const { updateAccount } = this.props

		updateAccount( {
			add_listen_time_seconds: time
		} )
	}

	updateNowPlaying() {
		const { getNowPlaying } = this.props

		// Get the GMT timestamp:
		const utcTimestampSeconds = getUtcTimestampSeconds()

		getNowPlaying( utcTimestampSeconds )
	}

	updatePlayer() {
		const { nowPlaying, playerOffset } = this.props

		if ( nowPlaying && nowPlaying.audio_file_url && ! nowPlaying.is_livestream ) {
			this.player.seekTo( playerOffset )
		}
	}

	rewind = () => {
		const currentSeekSeconds = this.player.getCurrentTime()
		const seekTo = currentSeekSeconds < 30 ? 0 : currentSeekSeconds - 30

		this.player.seekTo( seekTo )
	}

	fastForward = () => {
		const { nowPlaying, nowPlayingLive, canFastForward } = this.props
		const currentSeekSeconds = this.player.getCurrentTime()
		let seekTo = currentSeekSeconds + 30

		// Prevent seeking past the allowed hours:
		if ( ! canFastForward ) {
			// Get the offset between the program start time and now:
			const utcTimestampSeconds = getUtcTimestampSeconds()
			const offset = utcTimestampSeconds - nowPlaying.start_timestamp_gmt

			// Is the live offset (plus 30) after the current seek time?
			if ( ( offset + 30 ) >= currentSeekSeconds ) {
				seekTo = offset
			}
		}

		this.player.seekTo( seekTo )
	}

	// Fade will take 4 seconds to complete.
	fadeOut = () => {
		this.fadeInterval = setInterval( () => {
			this.reduceVolume()
		}, 200 )
	}

	reduceVolume = () => {
		const { isFullVolume } = this.props
		const { volume } = this.state

		if ( isFullVolume ) {
			return
		}

		if ( volume > 0 ) {
			this.setState( {
				volume: parseFloat( ( volume - 0.05 ).toFixed( 2 ) )
			} )
		} else {
			clearInterval( this.fadeInterval )
		}
	}

	onDuration = () => {
		// When the player finishes loading the file,
		// update the seek position.
		// Using the onDuration callback, as onReady fires every
		// time the seek function is called, not only after initial load.
		this.updatePlayer()
	}

	onTrackEnded = () => {
		const currentSeekSeconds = this.player.getCurrentTime()

		this.props.goToNextTrack( true )
	}

	onBuffer = () => {
		this.props.setBuffering( true )
	}

	onProgress = ( data ) => {
		const { isPlaying, isBuffering, setBuffering } = this.props

		// Buffering:
		if ( isPlaying && 0 === data.played ) {
			setBuffering( true )
		} else if ( isBuffering ) {
			setBuffering( false )
		}
	}

	onError = ( error ) => {
		if ( 20 == error.code ) {
			return
		}

		this.props.setMediaError( true )
	}

	render() {
		const { nowPlaying, isPlaying } = this.props
		const { playerReady, volume } = this.state

		return (
			<div className="site-player">
				{ nowPlaying &&
					<FilePlayer
						ref={ ( e ) => { this.player = e } }
						url={ nowPlaying.audio_file_url }
						playing={ isPlaying && playerReady }
						onDuration={ this.onDuration }
						onEnded={ this.onTrackEnded }
						onBuffer={ this.onBuffer }
						onProgress={ this.onProgress }
						onError={ this.onError }
						volume={ volume }
						playsinline={ true }
					/>
				}
			</div>
		)
	}
}

const mapStateToProps = ( state, ownProps ) => {
	return {
		isBuffering: state.schedule.isBuffering,
		isPlaying: state.schedule.isPlaying,
		isLive: state.schedule.isLive,
		isSeeking: state.schedule.isSeeking,
		isFadingOut: state.schedule.isFadingOut,
		isFullVolume: state.schedule.isFullVolume,
		mediaError: state.schedule.mediaError,
		canFastForward: state.schedule.canFastForward,
		showRewindWarning: state.schedule.showRewindWarning,
		seekTo: state.schedule.seekTo,
		seekToDirection: state.schedule.seekToDirection,
		seekSeconds: state.schedule.seekSeconds,
		nowPlaying: state.schedule.nowPlaying,
		nowPlayingLive: state.schedule.nowPlayingLive,
		playerOffset: state.schedule.playerOffset,
		siteId: state.schedule.siteId,
		timezoneType: state.schedule.timezoneType,
		timezoneOffset: state.schedule.timezoneOffset,
		userTimezoneOffset: state.schedule.userTimezoneOffset,
		secondsFromTrackStartToNow: state.schedule.secondsFromTrackStartToNow,
		seekSecondsForPercentCalcOnly: state.schedule.seekSecondsForPercentCalcOnly,
		accountData: state.site.accountData,
		isEmbedded: state.site.isEmbedded
	}
}

const mapDispatchToProps = ( dispatch, ownProps ) => {
	return {
		getNowPlaying( timestamp ) {
			dispatch( getNowPlaying( timestamp ) )
		},

		goToPreviousTrack() {
			dispatch( goToPreviousTrack() )
		},

		goToNextTrack( isAutoNext ) {
			dispatch( goToNextTrack( isAutoNext ) )
		},

		setBuffering( bool ) {
			dispatch( setBuffering( bool ) )
		},

		setMediaError( bool ) {
			dispatch( setMediaError( bool ) )
		},

		setRewindWarningState( bool ) {
			dispatch( setRewindWarningState( bool ) )
		},

		calculateSeekPercent() {
			dispatch( calculateSeekPercent() )
		},

		updateAccount( data ) {
			dispatch( updateAccount( data ) )
		}
	}
}

export default connect( mapStateToProps, mapDispatchToProps )( SitePlayer )
